/**
 * Which concept-test population should be selected? Spec 25 / increment 06, the tester's finding 1.
 *
 * An Ascent Protein attendee saw four vitamin/mom segments and "Running all 126 agents". 126 is the agent
 * count of `population_vitamin_survey_enriched_copy` — the hardcoded initial value of `selectedPopulation`.
 *
 * Scoping the `/vitamin/populations` proxy per organization fixes the LIST but not the SELECTION, because the
 * form only corrected the selection when the account matched an entry in the frontend's `clients.json`:
 *
 *     if (allowed) setSelectedPopulation(resolved[0].name);
 *     else         setSelectedPopulation(prev => prev || resolved[0].name);
 *
 * A summit attendee has no `clients.json` entry, so `allowed` is null and they take the `else` branch — where
 * `prev` is that hardcoded default and therefore truthy, so `||` keeps it forever. The result is a correct
 * list of one population and a selection pointing at something not in it, which is how segments and agents get
 * fetched for a population the account cannot use.
 *
 * The rule below asks a question about the DATA — "is the current selection actually available?" — instead of
 * a question about config membership. That covers the admin case the original branch existed for (a manual
 * choice survives while it is valid) without depending on whether we happen to recognise the client.
 */

export interface PopulationLike {
  name: string;
}

export interface PopulationSelectionInput {
  /** The populations this account may actually use, as resolved for the picker. */
  available: PopulationLike[];
  /** The currently selected population name — possibly the hardcoded initial default. */
  current: string;
}

export function resolvePopulationSelection({
  available,
  current,
}: PopulationSelectionInput): string {
  // An empty list means the fetch failed or returned nothing. Clearing the selection would turn a transient
  // network problem into lost form state, and "" is not a population anyone can run.
  if (!available.length) return current;

  // Valid selections are left alone. This is the admin's manual choice, and silently reassigning it would be
  // its own bug.
  if (current && available.some((p) => p.name === current)) return current;

  // Otherwise the selection is unusable — unset, or naming a population this account cannot reach. Move to
  // the first available one, which for a single-population account is the only one and is also what the
  // picker step skips straight past.
  return available[0].name;
}
